import type { EarthFaultLoopResponse, EarthFaultLoopWarning } from "../services/earthFaultLoop";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningCode = EarthFaultLoopWarning["code"];

// The group values are the shared ones of statusTone.GROUP_TONE, so study.css holds a rule
// and a tone for each: the links that are not the right evidence are listed under the
// "fault-level" group, the plain warnings under "other", the stated items under "not-evaluated".
type WarningGroup = "not-met" | "review-evidence" | "fault-level" | "other" | "not-evaluated";

interface EarthFaultLoopWarningPanelProps {
  result: EarthFaultLoopResponse;
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code without a label or a group is a compile error.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  DISCONNECTION_NOT_ASSURED: "Disconnection not assured",
  SYSTEM_KIND_REFUSED: "System kind refused",
  EXTERNAL_IMPEDANCE_NOT_ESTABLISHED: "External impedance Ze not established",
  EXTERNAL_IMPEDANCE_SOURCE_NOT_DECLARED: "Ze source not declared",
  FAULT_RUN_NOT_MINIMUM_EARTH_FAULT: "Fault run not the minimum earth-fault case",
  FAULT_RUN_BUS_MISMATCH: "Fault run bus differs from the board's bus",
  VOLTAGE_FACTOR_SOURCE_NOT_DECLARED: "Voltage factor source not declared",
  NOMINAL_VOLTAGE_SOURCE_NOT_DECLARED: "Nominal Uo source not declared",
  SYSTEM_KIND_NOT_ASSESSED: "System kind not assessed",
  CONDUCTOR_TEMPERATURE_NOT_DECLARED: "Conductor temperature not declared",
  CONDUCTOR_TEMPERATURE_SOURCE_NOT_DECLARED: "Temperature source not declared",
  CABLE_FIGURES_SOURCE_NOT_DECLARED: "Cable figures source not declared",
  CABLE_RUN_NOT_VALID: "Linked cable run not a passed design check",
  PROTECTIVE_CONDUCTOR_NOT_ESTABLISHED: "Protective conductor not established",
  DISCONNECTION_TIME_NOT_ESTABLISHED: "Required disconnection time not established",
  DISCONNECTION_TIME_SOURCE_NOT_DECLARED: "Disconnection time source not declared",
  DEVICE_OPERATING_CURRENT_NOT_ESTABLISHED: "Device operating current not established",
  DEVICE_CHARACTERISTIC_SOURCE_NOT_DECLARED: "Device characteristic source not declared",
  LIMIT_NOT_ESTABLISHED: "Limit not established (declared, not verified)",
  DECLARED_ZS_MAX_DIFFERS_FROM_IA: "Declared maximum Zs differs from the declared Ia",
  MARGIN_BELOW_DECLARED_MINIMUM: "Margin below the declared minimum",
  TEMPERATURE_ABOVE_INSULATION_MAXIMUM: "Temperature above the insulation maximum",
  REGISTER_TABLE_MEASURED_VALUE_BASIS: "Table F-3 basis (recommended measured values)",
  REGISTER_TABLE_NOT_APPLICABLE: "Table F-3 not applied",
  DECLARED_VALUE_GOVERNS_OVER_REGISTER_TABLE: "Declared value governs over Table F-3",
  REGISTER_TIME_BASIS: "Table F-2 time applied",
  REGISTER_TIME_NOT_APPLICABLE: "Table F-2 not applied",
  DECLARED_TIME_GOVERNS_OVER_REGISTER_TABLE: "Declared time governs over Table F-2",
  NOMINAL_VOLTAGE_NEAR_BAND_BOUNDARY: "Nominal Uo near a band edge of Table F-2",
  CABLE_RUN_NOT_LINKED: "Cable run not linked",
  RESISTANCE_DECLARED_GOVERNS: "Declared resistance governs",
  CABLE_REACTANCE_NOT_INCLUDED: "Cable reactance not included",
  ARITHMETIC_SUM_OF_MAGNITUDES: "Arithmetic sum of magnitudes",
  VOLTAGE_FACTOR_NOT_APPLIED: "Voltage factor not applied",
  DEVICE_CHARACTERISTIC_DECLARED: "Device characteristic declared",
  PROTECTIVE_CONDUCTOR_THERMAL_NOT_CHECKED: "Protective conductor thermal withstand not checked",
  TOUCH_VOLTAGE_NOT_EVALUATED: "Touch voltage not evaluated",
  ADDITIONAL_PROTECTION_NOT_ASSESSED: "Additional protection by RCD not assessed",
};

// Where each code is listed: the failed condition first, then the evidence the engineer
// owes, then the links that are not the right evidence, then what the engineer must look at,
// then what the sheet states (the F-1 lesson of IR-005: the not-checked items are always listed).
const WARNING_CODE_GROUPS: Record<WarningCode, WarningGroup> = {
  DISCONNECTION_NOT_ASSURED: "not-met",
  SYSTEM_KIND_REFUSED: "not-met",
  EXTERNAL_IMPEDANCE_NOT_ESTABLISHED: "review-evidence",
  EXTERNAL_IMPEDANCE_SOURCE_NOT_DECLARED: "review-evidence",
  FAULT_RUN_NOT_MINIMUM_EARTH_FAULT: "fault-level",
  FAULT_RUN_BUS_MISMATCH: "fault-level",
  VOLTAGE_FACTOR_SOURCE_NOT_DECLARED: "review-evidence",
  NOMINAL_VOLTAGE_SOURCE_NOT_DECLARED: "review-evidence",
  SYSTEM_KIND_NOT_ASSESSED: "review-evidence",
  CONDUCTOR_TEMPERATURE_NOT_DECLARED: "review-evidence",
  CONDUCTOR_TEMPERATURE_SOURCE_NOT_DECLARED: "review-evidence",
  CABLE_FIGURES_SOURCE_NOT_DECLARED: "review-evidence",
  CABLE_RUN_NOT_VALID: "fault-level",
  PROTECTIVE_CONDUCTOR_NOT_ESTABLISHED: "review-evidence",
  DISCONNECTION_TIME_NOT_ESTABLISHED: "review-evidence",
  DISCONNECTION_TIME_SOURCE_NOT_DECLARED: "review-evidence",
  DEVICE_OPERATING_CURRENT_NOT_ESTABLISHED: "review-evidence",
  DEVICE_CHARACTERISTIC_SOURCE_NOT_DECLARED: "review-evidence",
  LIMIT_NOT_ESTABLISHED: "review-evidence",
  DECLARED_ZS_MAX_DIFFERS_FROM_IA: "other",
  MARGIN_BELOW_DECLARED_MINIMUM: "other",
  TEMPERATURE_ABOVE_INSULATION_MAXIMUM: "other",
  REGISTER_TABLE_MEASURED_VALUE_BASIS: "not-evaluated",
  REGISTER_TABLE_NOT_APPLICABLE: "not-evaluated",
  DECLARED_VALUE_GOVERNS_OVER_REGISTER_TABLE: "not-evaluated",
  REGISTER_TIME_BASIS: "not-evaluated",
  REGISTER_TIME_NOT_APPLICABLE: "not-evaluated",
  DECLARED_TIME_GOVERNS_OVER_REGISTER_TABLE: "not-evaluated",
  NOMINAL_VOLTAGE_NEAR_BAND_BOUNDARY: "not-evaluated",
  CABLE_RUN_NOT_LINKED: "not-evaluated",
  RESISTANCE_DECLARED_GOVERNS: "not-evaluated",
  CABLE_REACTANCE_NOT_INCLUDED: "not-evaluated",
  ARITHMETIC_SUM_OF_MAGNITUDES: "not-evaluated",
  VOLTAGE_FACTOR_NOT_APPLIED: "not-evaluated",
  DEVICE_CHARACTERISTIC_DECLARED: "not-evaluated",
  PROTECTIVE_CONDUCTOR_THERMAL_NOT_CHECKED: "not-evaluated",
  TOUCH_VOLTAGE_NOT_EVALUATED: "not-evaluated",
  ADDITIONAL_PROTECTION_NOT_ASSESSED: "not-evaluated",
};

const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-met", title: "Disconnection not assured" },
  { group: "review-evidence", title: "Review required (evidence)" },
  { group: "fault-level", title: "Linked runs (review required)" },
  { group: "other", title: "Warnings" },
  { group: "not-evaluated", title: "Stated on the sheet (not checked here)" },
];

type ListedWarning = {
  key: string;
  code: WarningCode;
  message: string;
  prefix: string;
};

/** The study's warnings followed by every row's, in the engine's order, rows named. */
export function listWarnings(result: EarthFaultLoopResponse): ListedWarning[] {
  const study = result.warnings.map((warning, index) => ({
    key: `study-${warning.code}-${index}`,
    code: warning.code,
    message: warning.message,
    prefix: "",
  }));
  const rows = result.rows.flatMap((row) =>
    row.warnings.map((warning, index) => ({
      key: `${row.code}-${warning.code}-${index}`,
      code: warning.code,
      message: warning.message,
      prefix: `Circuit ${row.code}: `,
    })),
  );
  return [...study, ...rows];
}

export function EarthFaultLoopWarningPanel({ result }: EarthFaultLoopWarningPanelProps) {
  const warnings = listWarnings(result);

  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            const members = warnings.filter((warning) => WARNING_CODE_GROUPS[warning.code] === group);
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning) => (
                    <li key={warning.key} data-warning-code={warning.code}>
                      <strong>
                        {warning.prefix}
                        {WARNING_CODE_LABELS[warning.code]}
                      </strong>
                      {": "}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { GROUP_ORDER, WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default EarthFaultLoopWarningPanel;
